/**
 * ------------------------------------------------------------------------------------------------------------
 * CHARM PICKER (product page cross-sell)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Rail of charms related to the product being viewed, plus a detail popup. Markup comes from
 * snippets/charm-picker.liquid and snippets/charm-popup.liquid, which only load this file when the product
 * actually has charms.
 *
 * The rail itself carries no scrolling code: it is an overflow container, swiped on a phone and scrolled with
 * a trackpad on a laptop, which is why <scrollable-content draggable> is not used.
 *
 * What is reused rather than rewritten:
 *
 * - the popup is <modal-content> (theme.js), so the focus trap, Escape, the focus restore, the overlay and
 *   the scroll lock all come from the theme.
 * - the gallery indicators are <page-dots> (theme.js) with the theme's own .dots-nav markup.
 * - prices on the cards are rendered by Liquid; the running total is formatted with the theme's own
 *   formatMoney, exposed as window.themeHelpers.formatMoney (theme.js), so the shop money format is honoured
 *   in one place only.
 *
 * Adding to the cart is not done here: ticking a charm is enough, ProductForm._onSubmit (theme.js) collects
 * every checked add-on belonging to its form and sends one /cart/add.js request for the lot.
 */
(() => {
  // The section can be re-rendered (theme editor, cross-product swatch swap), which re-runs this file
  if (window.customElements.get('charm-picker')) {
    return;
  }

  // Mirrors the "sizes" the snippet prints on the cards, and is only used if that attribute goes missing
  const CARD_IMAGE_SIZES = '(max-width: 740px) 42vw, 180px';
  const CARD_IMAGE_WIDTHS = [120, 150, 180, 200, 240, 300, 360, 400, 480];

  // The gallery column is 55% of a 650px popup from tablet up, and the full width of a phone
  const GALLERY_IMAGE_SIZES = '(max-width: 740px) 100vw, 360px';
  const GALLERY_IMAGE_WIDTHS = [300, 400, 500, 600, 700, 800, 900];

  /**
   * Every picture in this markup was printed by Liquid's image_url with a "width" parameter, so another size
   * of the same picture is that same URL with the parameter rewritten.
   *
   * The theme's own getSizedMediaUrl (theme.js:3506) does not fit here: it builds the legacy "file_400x.jpg"
   * form, and its regex only matches a URL ending in the extension or in "?v=123", never one that already
   * carries "&width=".
   */
  const sizedUrl = (url, width) => url.replace(/([?&]width=)\d+/, `$1${width}`);

  const srcsetFor = (url, widths) => widths.map((width) => `${sizedUrl(url, width)} ${width}w`).join(', ');

  class CharmPicker extends HTMLElement {
    connectedCallback() {
      // Being moved rather than removed reconnects the element without disconnecting it first
      this.abortController?.abort();
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.cards = Array.from(this.querySelectorAll('.charm-card'));
      this.popup = this.querySelector('.charm-popup');
      this.totalElement = this.querySelector('[data-charm-total]');
      this.totalValueElement = this.querySelector('[data-charm-total-value]');

      if (this.cards.length === 0 || !this.popup) {
        return;
      }

      this.gallery = this.popup.querySelector('[data-charm-gallery]');
      this.dotsContainer = this.popup.querySelector('[data-charm-dots]');
      this.thumbs = this.popup.querySelector('[data-charm-thumbs]');
      this.popupTitle = this.popup.querySelector('.charm-popup__title');
      this.popupPrice = this.popup.querySelector('[data-charm-popup-price]');
      this.addButton = this.popup.querySelector('[data-charm-add]');

      this.mediaCache = new Map();
      this.activeIndex = null;
      this.dotsFrame = null;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('change', this._onChange.bind(this), { signal });

      this.gallery.addEventListener('scroll', this._onGalleryScroll.bind(this), { passive: true, signal });
      this.dotsContainer.addEventListener('page-dots:changed', (event) => this._scrollGalleryTo(event.detail.index), { signal });
      this.popup.addEventListener('openable-element:close', this._onPopupClose.bind(this), { signal });

      // "variant:change" is dispatched on the <form> and bubbles (theme.js), and <variant-picker> is a sibling
      // of that form rather than a child of it, so document is where both are reachable from
      document.addEventListener('variant:change', this._onVariantChange.bind(this), { signal });
      document.addEventListener('variant:added', this._onVariantAdded.bind(this), { signal });

      this._updateTotal();
    }

    disconnectedCallback() {
      this.abortController?.abort();

      if (this.dotsFrame) {
        cancelAnimationFrame(this.dotsFrame);
        this.dotsFrame = null;
      }
    }

    get productForm() {
      return document.getElementById(this.getAttribute('form-id'));
    }

    /**
     * The event carries "detail.formId", but that is not the form's id: on a <form>, "form.id" resolves to the
     * control named "id" - here the hidden input holding the variant, which the theme itself relies on
     * (theme.js:5421). The id is therefore read off the element the event was dispatched on.
     */
    _belongsToOurForm(event) {
      return event.target instanceof HTMLFormElement && event.target.getAttribute('id') === this.getAttribute('form-id');
    }

    /* --------------------------------------------------------------------------------------------- events */

    _onClick(event) {
      const openButton = event.target.closest('[data-charm-open]');

      if (openButton) {
        const card = openButton.closest('.charm-card');

        // <modal-content> restores focus to whatever held it when the popup opened. Opening from the picture
        // would make that a button hidden from assistive technology, so the labelled title takes it instead.
        card.querySelector('.charm-card__title')?.focus();

        this._openPopup(Number(card.dataset.charmIndex));
        return;
      }

      const thumb = event.target.closest('[data-charm-thumb]');

      if (thumb) {
        this._showCharm(Number(thumb.dataset.charmThumb));
        return;
      }

      if (event.target.closest('[data-charm-add]')) {
        this._addActiveCharm();
      }
    }

    _onChange(event) {
      if (event.target.matches('.charm-card__checkbox')) {
        this._updateTotal();
      }
    }

    _onVariantChange(event) {
      const variant = event.detail?.variant;

      if (!variant || !this._belongsToOurForm(event)) {
        return;
      }

      const imagesElement = this.querySelector(`[data-charm-variant-images="${variant.id}"]`);

      // No list printed for this variant means nothing is known about it, which is not the same as knowing it
      // has no pictures: leave the cards as they are
      if (!imagesElement) {
        return;
      }

      let images = [];

      try {
        images = JSON.parse(imagesElement.textContent) || [];
      } catch (error) {
        return;
      }

      this.cards.forEach((card, index) => {
        const source = images[index] || card.dataset.charmFallbackImage,
              wrapper = card.querySelector('[data-charm-image]'),
              image = wrapper.querySelector('img');

        if (!source) {
          wrapper.replaceChildren();
          return;
        }

        if (image) {
          image.src = source;
          // srcset outranks src, so leaving a stale one behind would keep the previous variant on screen
          image.srcset = srcsetFor(source, CARD_IMAGE_WIDTHS);
        } else {
          wrapper.replaceChildren(this._createCardImage(source));
        }
      });

      if (this.popup.open) {
        this._renderThumbs();
      }
    }

    _onVariantAdded(event) {
      if (!this._belongsToOurForm(event)) {
        return;
      }

      // The charms went into the cart with the product, so the rail starts over rather than adding them twice
      this.cards.forEach((card) => {
        const checkbox = card.querySelector('.charm-card__checkbox');

        if (checkbox) {
          checkbox.checked = false;
        }
      });

      this._updateTotal();
    }

    _onPopupClose() {
      this.gallery.querySelectorAll('video').forEach((video) => video.pause());
    }

    /* ---------------------------------------------------------------------------------------------- popup */

    _openPopup(index) {
      this.activeIndex = index;

      this._renderThumbs();
      this._showCharm(index);

      this.popup.open = true;
    }

    _showCharm(index) {
      const card = this.cards[index];

      if (!card) {
        return;
      }

      this.activeIndex = index;

      const price = card.querySelector('[data-charm-price]'),
            checkbox = card.querySelector('.charm-card__checkbox');

      this.popupTitle.textContent = this._titleFor(card);
      this.popupPrice.replaceChildren(price ? price.cloneNode(true) : document.createDocumentFragment());
      this.addButton.disabled = !checkbox || checkbox.disabled;

      this._renderGallery(card);

      this.thumbs.querySelectorAll('[data-charm-thumb]').forEach((thumb) => {
        thumb.setAttribute('aria-current', Number(thumb.dataset.charmThumb) === index ? 'true' : 'false');
      });
    }

    _addActiveCharm() {
      const card = this.cards[this.activeIndex],
            checkbox = card?.querySelector('.charm-card__checkbox');

      if (!checkbox || checkbox.disabled) {
        return;
      }

      checkbox.checked = true;
      this._updateTotal();

      // Closed first, so <modal-content> hands focus back to the card title before the form takes over
      this.popup.open = false;
      this.productForm?.requestSubmit();
    }

    _titleFor(card) {
      return card.querySelector('[data-charm-open]').textContent.trim();
    }

    _createCardImage(source) {
      const image = document.createElement('img');

      image.src = source;
      image.srcset = srcsetFor(source, CARD_IMAGE_WIDTHS);
      image.sizes = this.getAttribute('image-sizes') || CARD_IMAGE_SIZES;
      image.width = 400;
      image.height = 400;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';

      return image;
    }

    /* -------------------------------------------------------------------------------------------- gallery */

    _mediaFor(card) {
      const index = card.dataset.charmIndex;

      // The JSON is only read the first time a charm is shown, never when the page loads
      if (!this.mediaCache.has(index)) {
        let media = [];

        try {
          media = JSON.parse(card.querySelector('[data-charm-media]').textContent) || [];
        } catch (error) {
          media = [];
        }

        this.mediaCache.set(index, media);
      }

      return this.mediaCache.get(index);
    }

    _renderGallery(card) {
      const slides = document.createDocumentFragment();

      this._mediaFor(card).forEach((item) => {
        if (!item.src) {
          return;
        }

        const slide = document.createElement('div');

        slide.className = 'charm-popup__slide';

        if (item.type === 'video') {
          const video = document.createElement('video');

          video.src = item.src;
          video.controls = true;
          video.playsInline = true;
          // Every video slide has a poster, so nothing of the file is fetched until it is actually played
          video.preload = 'none';

          if (item.poster) {
            video.poster = item.poster;
          }

          slide.appendChild(video);
        } else {
          const image = document.createElement('img'),
                // Candidates wider than the picture itself would all resolve to the same bytes
                widths = item.width ? GALLERY_IMAGE_WIDTHS.filter((width) => width <= item.width) : GALLERY_IMAGE_WIDTHS;

          image.src = item.src;
          image.alt = item.alt || '';
          image.loading = 'lazy';
          image.decoding = 'async';

          if (widths.length > 0) {
            image.srcset = srcsetFor(item.src, widths);
            image.sizes = GALLERY_IMAGE_SIZES;
          }

          if (item.width && item.height) {
            image.width = item.width;
            image.height = item.height;
          }

          slide.appendChild(image);
        }

        slides.appendChild(slide);
      });

      this.gallery.replaceChildren(slides);
      this.gallery.scrollLeft = 0;

      this._renderDots(this.gallery.childElementCount);
    }

    _renderDots(count) {
      if (count < 2) {
        this.dotsContainer.replaceChildren();
        return;
      }

      // <page-dots> caches its buttons in connectedCallback, so each gallery gets a brand new element rather
      // than new buttons inside the previous one
      const dots = document.createElement('page-dots'),
            labelTemplate = this.dotsContainer.getAttribute('data-slide-label') || '';

      dots.className = 'dots-nav dots-nav--centered';

      for (let index = 0; index < count; index++) {
        const button = document.createElement('button'),
              label = document.createElement('span');

        button.type = 'button';
        button.className = 'dots-nav__item tap-area';
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');

        label.className = 'visually-hidden';
        label.textContent = labelTemplate.replace('@num@', index + 1);

        button.appendChild(label);
        dots.appendChild(button);
      }

      this.dotsContainer.replaceChildren(dots);
    }

    _scrollGalleryTo(index) {
      const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;

      this.gallery.scrollTo({ left: direction * index * this.gallery.clientWidth, behavior: 'smooth' });
    }

    _onGalleryScroll() {
      if (this.dotsFrame) {
        return;
      }

      this.dotsFrame = requestAnimationFrame(() => {
        this.dotsFrame = null;

        const dots = this.dotsContainer.firstElementChild,
              width = this.gallery.clientWidth;

        if (!dots || !width) {
          return;
        }

        const index = Math.round(Math.abs(this.gallery.scrollLeft) / width);

        if (dots.selectedIndex !== index) {
          dots.selectedIndex = index;
        }
      });
    }

    /* --------------------------------------------------------------------------------------- thumbs/total */

    _renderThumbs() {
      const thumbs = document.createDocumentFragment();

      this.cards.forEach((card, index) => {
        const button = document.createElement('button'),
              label = document.createElement('span'),
              image = card.querySelector('[data-charm-image] img');

        button.type = 'button';
        button.className = 'charm-popup__thumb';
        button.dataset.charmThumb = index;
        button.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');

        if (image) {
          const clone = image.cloneNode();

          clone.removeAttribute('loading');
          button.appendChild(clone);
        }

        label.className = 'visually-hidden';
        label.textContent = this._titleFor(card);
        button.appendChild(label);

        thumbs.appendChild(button);
      });

      this.thumbs.replaceChildren(thumbs);
    }

    _updateTotal() {
      const formatMoney = window.themeHelpers?.formatMoney;

      let total = 0,
          selected = 0;

      this.cards.forEach((card) => {
        const checkbox = card.querySelector('.charm-card__checkbox'),
              isSelected = Boolean(checkbox?.checked);

        card.classList.toggle('is-selected', isSelected);

        if (isSelected) {
          selected++;
          total += Number(card.dataset.charmPriceCents) || 0;
        }
      });

      if (!this.totalElement) {
        return;
      }

      this.totalElement.hidden = selected === 0 || typeof formatMoney !== 'function';

      if (!this.totalElement.hidden) {
        this.totalValueElement.innerHTML = formatMoney(total);
      }
    }
  }

  window.customElements.define('charm-picker', CharmPicker);
})();
